import { Component, Input } from '@angular/core';

export type IconName =
  | 'dashboard'
  | 'clipboard-list'
  | 'wrench'
  | 'boxes'
  | 'warehouse'
  | 'package'
  | 'cart'
  | 'truck'
  | 'server'
  | 'building'
  | 'sliders'
  | 'users'
  | 'history'
  | 'mail'
  | 'bell'
  | 'settings'
  | 'bot'
  | 'network'
  | 'log-out'
  | 'plus'
  | 'pencil'
  | 'trash'
  | 'search'
  | 'download'
  | 'upload'
  | 'check'
  | 'x'
  | 'restore'
  | 'toggle'
  | 'palette'
  | 'chevron-left'
  | 'chevron-right'
  | 'menu'
  | 'briefcase'
  | 'clock'
  | 'eye'
  | 'chevron-down'
  | 'globe'
  | 'tag'
  | 'git-branch'
  | 'shield-check';

/**
 * Invento Design System specifies Lucide (github.com/lucide-icons/lucide) as its icon set —
 * outline style, stroke-width 1.75 (--icon-stroke). No CDN/package is wired in, and this app has
 * no network access to fetch the real Lucide SVGs at build time, so these are drawn to match that
 * spec (24x24 viewBox, stroke=currentColor, round caps/joins) rather than lifted from the library
 * verbatim — swap for @lucide/angular or the real SVGs if/when that dependency is added.
 */
@Component({
  selector: 'app-icon',
  standalone: true,
  template: `
    <svg
      [attr.width]="size"
      [attr.height]="size"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      @switch (name) {
        @case ('dashboard') {
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        }
        @case ('clipboard-list') {
          <rect x="5" y="4" width="14" height="17" rx="2" />
          <rect x="9" y="2" width="6" height="4" rx="1" />
          <line x1="8" y1="10" x2="16" y2="10" />
          <line x1="8" y1="14" x2="16" y2="14" />
          <line x1="8" y1="18" x2="12" y2="18" />
        }
        @case ('wrench') {
          <rect x="6" y="3" width="12" height="18" rx="2" />
          <path d="M9 3v2h6V3" />
          <line x1="9" y1="10" x2="15" y2="10" />
          <line x1="9" y1="14" x2="15" y2="14" />
          <line x1="9" y1="18" x2="13" y2="18" />
        }
        @case ('boxes') {
          <rect x="3" y="8" width="18" height="13" rx="1" />
          <path d="M3 8l9-5 9 5" />
          <line x1="12" y1="8" x2="12" y2="21" />
        }
        @case ('warehouse') {
          <path d="M3 21V9l9-6 9 6v12" />
          <line x1="3" y1="21" x2="21" y2="21" />
          <rect x="9" y="13" width="6" height="8" />
        }
        @case ('package') {
          <path d="M12 2 3 7v10l9 5 9-5V7z" />
          <path d="M3 7l9 5 9-5" />
          <line x1="12" y1="12" x2="12" y2="22" />
        }
        @case ('cart') {
          <circle cx="9" cy="20" r="1" />
          <circle cx="18" cy="20" r="1" />
          <path d="M3 4h2l2.4 12.4A2 2 0 0 0 9.4 18H18a2 2 0 0 0 2-1.6L21 8H6" />
        }
        @case ('truck') {
          <rect x="1" y="7" width="13" height="10" rx="1" />
          <path d="M14 10h4l3 3v4h-7z" />
          <circle cx="5" cy="18" r="1.5" />
          <circle cx="17" cy="18" r="1.5" />
        }
        @case ('server') {
          <rect x="3" y="4" width="18" height="6" rx="1" />
          <rect x="3" y="14" width="18" height="6" rx="1" />
          <circle cx="7" cy="7" r="0.6" fill="currentColor" stroke="none" />
          <circle cx="7" cy="17" r="0.6" fill="currentColor" stroke="none" />
        }
        @case ('building') {
          <rect x="4" y="2" width="16" height="20" rx="1" />
          <rect x="8" y="6" width="2" height="2" />
          <rect x="14" y="6" width="2" height="2" />
          <rect x="8" y="11" width="2" height="2" />
          <rect x="14" y="11" width="2" height="2" />
          <rect x="9" y="17" width="6" height="5" />
        }
        @case ('sliders') {
          <line x1="4" y1="6" x2="20" y2="6" />
          <circle cx="14" cy="6" r="2" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <circle cx="8" cy="12" r="2" />
          <line x1="4" y1="18" x2="20" y2="18" />
          <circle cx="16" cy="18" r="2" />
        }
        @case ('users') {
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6" />
          <circle cx="18" cy="9" r="2.4" />
          <path d="M15.5 14.2c2.6.5 4.5 2.7 4.5 5.8" />
        }
        @case ('history') {
          <path d="M6 2h9l5 5v15H6z" />
          <path d="M15 2v5h5" />
          <line x1="9" y1="12" x2="15" y2="12" />
          <line x1="9" y1="16" x2="15" y2="16" />
        }
        @case ('mail') {
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 6l9 7 9-7" />
        }
        @case ('bell') {
          <path
            d="M12 3a5 5 0 0 0-5 5v3.5c0 1-.4 2-1.2 2.8L4 16h16l-1.8-1.7c-.8-.8-1.2-1.8-1.2-2.8V8a5 5 0 0 0-5-5z"
          />
          <path d="M9.5 19a2.5 2.5 0 0 0 5 0" />
        }
        @case ('settings') {
          <circle cx="12" cy="12" r="3" />
          <path
            d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"
          />
        }
        @case ('bot') {
          <rect x="5" y="8" width="14" height="10" rx="2" />
          <line x1="12" y1="4" x2="12" y2="8" />
          <circle cx="12" cy="3" r="1" />
          <circle cx="9" cy="13" r="1.2" />
          <circle cx="15" cy="13" r="1.2" />
          <line x1="9" y1="16" x2="15" y2="16" />
        }
        @case ('network') {
          <circle cx="6" cy="6" r="3" />
          <circle cx="18" cy="6" r="3" />
          <circle cx="12" cy="18" r="3" />
          <line x1="7.8" y1="8.2" x2="10.6" y2="15.5" />
          <line x1="16.2" y1="8.2" x2="13.4" y2="15.5" />
        }
        @case ('log-out') {
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        }
        @case ('plus') {
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        }
        @case ('pencil') {
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
        }
        @case ('trash') {
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
        }
        @case ('search') {
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        }
        @case ('download') {
          <path d="M12 3v12" />
          <polyline points="7 11 12 16 17 11" />
          <path d="M5 19h14" />
        }
        @case ('upload') {
          <path d="M12 3v12" />
          <polyline points="7 8 12 3 17 8" />
          <path d="M5 21h14" />
        }
        @case ('check') {
          <polyline points="20 6 9 17 4 12" />
        }
        @case ('x') {
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        }
        @case ('restore') {
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <polyline points="3 4 3 9 8 9" />
        }
        @case ('toggle') {
          <rect x="2" y="7" width="20" height="10" rx="5" />
          <circle cx="16" cy="12" r="3.2" fill="currentColor" stroke="none" />
        }
        @case ('palette') {
          <path d="M12 3a9 9 0 1 0 0 18c1 0 1.5-.7 1.5-1.5 0-.4-.15-.75-.4-1.05-.25-.3-.4-.65-.4-1.05 0-.8.65-1.4 1.4-1.4H16a4 4 0 0 0 4-4c0-4.7-3.6-9-8-9Z" />
          <circle cx="7.5" cy="10.5" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="11" cy="7" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="15.5" cy="7.5" r="1.1" fill="currentColor" stroke="none" />
        }
        @case ('chevron-left') {
          <polyline points="15 18 9 12 15 6" />
        }
        @case ('chevron-right') {
          <polyline points="9 18 15 12 9 6" />
        }
        @case ('menu') {
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="18" x2="20" y2="18" />
        }
        @case ('briefcase') {
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
          <line x1="2" y1="13" x2="22" y2="13" />
        }
        @case ('clock') {
          <circle cx="12" cy="12" r="9" />
          <polyline points="12 7 12 12 15 15" />
        }
        @case ('eye') {
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        }
        @case ('chevron-down') {
          <polyline points="6 9 12 15 18 9" />
        }
        @case ('globe') {
          <circle cx="12" cy="12" r="9" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <path d="M12 3a13 13 0 0 1 0 18a13 13 0 0 1 0-18Z" />
        }
        @case ('tag') {
          <path d="M20.6 12.6 12 21.2a2 2 0 0 1-2.8 0l-6.4-6.4a2 2 0 0 1 0-2.8L11.4 3.4A2 2 0 0 1 12.8 3H19a2 2 0 0 1 2 2v6.2a2 2 0 0 1-.4 1.4Z" />
          <circle cx="15.5" cy="7.5" r="1.4" fill="currentColor" stroke="none" />
        }
        @case ('git-branch') {
          <line x1="6" y1="3" x2="6" y2="15" />
          <circle cx="18" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <path d="M18 9a9 9 0 0 1-9 9" />
        }
      }
    </svg>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        flex: none;
      }
    `
  ]
})
export class IconComponent {
  @Input() name!: IconName;
  @Input() size = 18;
}
